import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../../../shared/libs/redux';
import { useUnbindPrivateTemplateTag } from '../../../../template/private/api/useUnbindPrivateTemplateTag';
import { usePrivateTemplateTagsData } from '../../../../../../../../shared/libs/templates/private';
import { IPrivateTemplateData } from '../../../../../../../../entities/template/types';
import { useBindTemplateTag } from '../../../../../../../../features/template/tag/bind/api/useBindTemplateTag';
import {
	setCurrentTemplateTagsIds
} from '../../../../../../../../entities/template/private-template/model/privateTemplateSlice';

export const useSelectedTemplateTags = (template: IPrivateTemplateData) => {
	const dispatch = useAppDispatch();

	const { id } = template;
	const { tags } = usePrivateTemplateTagsData();
	const { unbindTag } = useUnbindPrivateTemplateTag(id || '');
	const bindTemplateTag = useBindTemplateTag('PRIVATE_TEMPLATE', id);

	const selectedTags = useAppSelector(state => state.privateTemplate.currentTemplateTagsIds);

	const onSetCurrentIds = (preparedIds: number[]) => {
		dispatch(setCurrentTemplateTagsIds(preparedIds));
	};

	const onTagClick = async (id: number) => {
		const tag = tags?.templateTags?.find(tag => tag.id === id);
		if (!tag) return;

		if (selectedTags.includes(id)) {
			const updatedIds = selectedTags.filter(tag => tag !== id);
			onSetCurrentIds(updatedIds);
			await unbindTag(id);
		} else {
			const updatedIds = [...selectedTags, id];
			onSetCurrentIds(updatedIds);
			await bindTemplateTag(tag);
		}
	};

	useEffect(() => {
		if (template.tags?.fromTemplates) {
			const initialSelectedTags = template.tags.fromTemplates.map(tag => tag.id);

			if (JSON.stringify(selectedTags) !== JSON.stringify(initialSelectedTags)) {
				onSetCurrentIds(initialSelectedTags);
			}
		}
	}, [template.tags?.fromTemplates]);

	const onRemoveTag = (tagId: number) => {
		const preparedIds = selectedTags.filter(id => id !== tagId);
		onSetCurrentIds(preparedIds);
	};

	const onCleanSelectedTagList = () => onSetCurrentIds([]);

	const onCleanSelectedTemplateTags = (templateId: string | undefined) => {
		if (!templateId || !tags?.templateTags) return;

		if (template?.tags?.fromTemplates) {
			const tagsToUnbind = template.tags.fromTemplates.map(tag => tag.id);
			Promise.all(tagsToUnbind.map(tagId => unbindTag(tagId)));
		}

		onSetCurrentIds([]);
	};

	return {
		tags,
		selectedTags,
		onTagClick,
		onRemoveTag,
		onCleanSelectedTagList,
		onCleanSelectedTemplateTags,
	};
};
